import { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import Preloader from "../../shared/PreLoaders";
import { logActivity } from "../../utils/LogActivity";
import { AuthContext } from "../../Context/AuthContext";
import { Authorization } from "../../Context/Authorization";

/* ── Reusable styled field ─────────────────────────────────── */
const inputStyle = {
  width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0",
  borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff",
  outline: "none", transition: "border-color 0.2s",
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "block" };
const errorStyle = { fontSize: 11, color: "#C62828", marginTop: 4 };

const Field = ({ label, error, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={labelStyle}>{label}</label>
    {children}
    {error && <div style={errorStyle}>{error}</div>}
  </div>
);

const Input = ({ error, ...props }) => (
  <input
    style={{ ...inputStyle, borderColor: error ? "#E53E3E" : "#E2E8F0" }}
    onFocus={(e) => e.target.style.borderColor = "#4CAF50"}
    onBlur={(e) => e.target.style.borderColor = error ? "#E53E3E" : "#E2E8F0"}
    {...props}
  />
);

const Select = ({ error, children, ...props }) => (
  <select
    style={{ ...inputStyle, borderColor: error ? "#E53E3E" : "#E2E8F0", cursor: "pointer" }}
    onFocus={(e) => e.target.style.borderColor = "#4CAF50"}
    onBlur={(e) => e.target.style.borderColor = error ? "#E53E3E" : "#E2E8F0"}
    {...props}
  >
    {children}
  </select>
);

/* ── Section header ────────────────────────────────────────── */
const Section = ({ icon, title, children, cols = 2 }) => (
  <div style={{ marginBottom: 24 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, paddingBottom: 10, borderBottom: "1px solid #EDF2F7" }}>
      <span style={{ width: 28, height: 28, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13, flexShrink: 0 }}>
        <i className={`mdi ${icon}`} />
      </span>
      <span style={{ fontSize: 13, fontWeight: 700, color: "#2D3748" }}>{title}</span>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))`, gap: "0 20px" }}>
      {children}
    </div>
  </div>
);

/* ── EditMember ─────────────────────────────────────────────── */
const EditMember = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userId, selectedJfmcType } = useContext(AuthContext);
  const Api = process.env.REACT_APP_API;

  const [formData, setFormData] = useState({
    name: "", age: "", gender: "", vfcCode: "",
    fatherOrHusband: "", fatherOrHusbandName: "",
    address: "", village: "", panchayath: "", thaluk: "", pinCode: "", district: "",
    dob: "", phone: "", aadhar: "", rationCard: "",
    bank: "", branch: "", accountNumber: "", ifscCode: "",
    eMail: "", community: "", work: "", educationalQualification: "",
    income: "", whatsAppNo: "", maritialStatus: "", familyMemberDetails: "",
    profilePicture: "", signature: "",
  });

  const [errors, setErrors]         = useState({});
  const [loading, setLoading]       = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [vfcOptions, setVfcOptions] = useState([]);

  /* ── Fetch member data on mount ── */
  useEffect(() => {
    const fetchMember = async () => {
      setLoading(true);
      try {
        const config = Authorization();
        const res = await axios.get(`${Api}/api/members/${id}`, config);
        const member = res.data.member;
        setFormData({
          ...member,
          dob: member.dob ? new Date(member.dob).toISOString().split("T")[0] : "",
        });
      } catch (err) {
        console.error("Error fetching member data", err);
        toast.error("Failed to fetch member data.");
      } finally {
        setLoading(false);
      }
    };
    fetchMember();
  }, [id, Api]);

  /* ── Fetch VFCs when global JFMC type is set ── */
  useEffect(() => {
    if (!selectedJfmcType?._id) { setVfcOptions([]); return; }
    const fetchVfcs = async () => {
      try {
        const config = Authorization();
        const res = await axios.post(`${Api}/api/vfcs/names`, { jfmcTypeId: selectedJfmcType._id }, config);
        setVfcOptions(res.data.vfcs || []);
      } catch (e) {
        console.error("Error fetching VFCs", e);
        setVfcOptions([]);
      }
    };
    fetchVfcs();
  }, [Api, selectedJfmcType]);

  /* ── Validation ── */
  const validate = (d) => {
    const e = {};
    if (!d.name)                e.name = "Name is required";
    if (!d.age)                 e.age = "Age is required";
    if (!d.gender)              e.gender = "Gender is required";
    if (!d.vfcCode)             e.vfcCode = "JFMC Name is required";
    if (!d.fatherOrHusband)     e.fatherOrHusband = "Selection is required";
    if (!d.fatherOrHusbandName) e.fatherOrHusbandName = "Name is required";
    if (!d.address)             e.address = "Address is required";
    if (!d.village)             e.village = "Village is required";
    if (!d.panchayath)          e.panchayath = "Panchayath is required";
    if (!d.thaluk)              e.thaluk = "Thaluk is required";
    if (!d.pinCode)             e.pinCode = "Pin code is required";
    if (!d.district)            e.district = "District is required";
    return e;
  };

  const handleChange = (e) => {
    const { name, type, value, files } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === "file" ? files[0] : value }));
    if (errors[name]) setErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error("Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      if (formData.profilePicture instanceof File) fd.append("profilePicture", formData.profilePicture);
      if (formData.signature instanceof File)      fd.append("signature", formData.signature);
      for (const key in formData) {
        if (key !== "profilePicture" && key !== "signature") {
          fd.append(key, formData[key] ?? "");
        }
      }
      const config = Authorization();
      const axiosConfig = {
        ...config,
        headers: { ...config.headers, "Content-Type": "multipart/form-data" },
      };
      await axios.put(`${Api}/api/members/${id}`, fd, axiosConfig);
      toast.success("Member updated successfully!");
      await logActivity(userId, "Edited Member", "Edit Member Page", Api);
      navigate("/member/manage");
    } catch (err) {
      console.error("Error updating member", err);
      toast.error("Failed to update member");
    } finally {
      setSubmitting(false);
    }
  };

  /* ════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════ */
  return (
    <div>
      {loading && <Preloader loading={loading} />}

      {/* ── Page Header ── */}
      <div style={{ marginBottom: 20 }}>
        <h4 style={{ margin: 0, fontWeight: 700, color: "#1A202C", fontSize: 18 }}>Edit Member</h4>
        <div style={{ fontSize: 12, color: "#718096", marginTop: 3 }}>
          <span style={{ color: "#A0AEC0" }}>Member</span>
          <span style={{ margin: "0 6px", color: "#CBD5E0" }}>›</span>
          <span style={{ color: "#2E7D32", fontWeight: 600 }}>Edit Registration</span>
        </div>
      </div>

      {/* ── No JFMC type selected warning ── */}
      {!selectedJfmcType && (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #FED7D7", padding: "32px 24px", textAlign: "center", marginBottom: 20 }}>
          <div style={{ width: 52, height: 52, background: "#FFF5F5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
            <i className="mdi mdi-tag-outline" style={{ fontSize: 24, color: "#E53E3E" }} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>JFMC Type not selected</div>
          <div style={{ fontSize: 12, color: "#A0AEC0" }}>Use the <strong>JFMC Type</strong> selector in the top navigation bar to choose a type before editing a member.</div>
        </div>
      )}

      {/* ── Form Card ── */}
      {selectedJfmcType && (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>

          {/* Card header */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", borderBottom: "1px solid #EDF2F7", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
            <span style={{ width: 36, height: 36, background: "rgba(255,255,255,0.15)", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 18 }}>
              <i className="mdi mdi-account-edit" />
            </span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>Member Edit Form</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>Update the member registration details</div>
            </div>
          </div>

          {/* Form body */}
          <form onSubmit={handleSubmit} style={{ padding: "24px" }}>

            {/* ── Section: Personal Info ── */}
            <Section icon="mdi-account-outline" title="Personal Information">
              <Field label={<>Name <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.name}>
                <Input name="name" placeholder="Enter full name" value={formData.name} onChange={handleChange} error={errors.name} />
              </Field>
              <Field label={<>Age <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.age}>
                <Input name="age" type="number" placeholder="Enter age" value={formData.age} onChange={handleChange} error={errors.age} />
              </Field>
              <Field label={<>Gender <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.gender}>
                <Select name="gender" value={formData.gender} onChange={handleChange} error={errors.gender}>
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="other">Other</option>
                </Select>
              </Field>
              <Field label="Date of Birth" error={errors.dob}>
                <Input name="dob" type="date" value={formData.dob} onChange={handleChange} error={errors.dob} />
              </Field>
              <Field label="Marital Status" error={errors.maritialStatus}>
                <Select name="maritialStatus" value={formData.maritialStatus} onChange={handleChange}>
                  <option value="">Select Status</option>
                  <option value="Single">Single</option>
                  <option value="Married">Married</option>
                  <option value="Widowed">Widowed</option>
                  <option value="Divorced">Divorced</option>
                </Select>
              </Field>
              <Field label="Community" error={errors.community}>
                <Input name="community" placeholder="Enter community" value={formData.community} onChange={handleChange} />
              </Field>
            </Section>

            {/* ── Section: JFMC Assignment ── */}
            <Section icon="mdi-home-group" title="JFMC Assignment">
              <Field label={<>JFMC Name <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.vfcCode}>
                <Select
                  name="vfcCode"
                  value={formData.vfcCode?._id || formData.vfcCode}
                  onChange={handleChange}
                  error={errors.vfcCode}
                >
                  <option value="">— Select JFMC —</option>
                  {vfcOptions.sort((a, b) => a.vfcName.localeCompare(b.vfcName)).map(v => (
                    <option key={v._id} value={v._id}>{v.vfcName}</option>
                  ))}
                </Select>
              </Field>
              <div /> {/* empty cell to keep 2-col grid */}
            </Section>

            {/* ── Section: Family ── */}
            <Section icon="mdi-account-supervisor-outline" title="Family Details">
              <Field label={<>Father / Husband <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.fatherOrHusband}>
                <Select name="fatherOrHusband" value={formData.fatherOrHusband} onChange={handleChange} error={errors.fatherOrHusband}>
                  <option value="">Select Relation</option>
                  <option value="Father">Father</option>
                  <option value="Husband">Husband</option>
                </Select>
              </Field>
              <Field label={<>Father / Husband Name <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.fatherOrHusbandName}>
                <Input name="fatherOrHusbandName" placeholder="Enter name" value={formData.fatherOrHusbandName} onChange={handleChange} error={errors.fatherOrHusbandName} />
              </Field>
              <Field label="Family Member Details" error={errors.familyMemberDetails}>
                <Input name="familyMemberDetails" placeholder="Enter family member details" value={formData.familyMemberDetails} onChange={handleChange} />
              </Field>
            </Section>

            {/* ── Section: Address ── */}
            <Section icon="mdi-map-marker-outline" title="Address">
              <Field label={<>Address <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.address}>
                <Input name="address" placeholder="Enter address" value={formData.address} onChange={handleChange} error={errors.address} />
              </Field>
              <Field label={<>Village <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.village}>
                <Input name="village" placeholder="Enter village" value={formData.village} onChange={handleChange} error={errors.village} />
              </Field>
              <Field label={<>Panchayath <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.panchayath}>
                <Input name="panchayath" placeholder="Enter panchayath" value={formData.panchayath} onChange={handleChange} error={errors.panchayath} />
              </Field>
              <Field label={<>Taluk <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.thaluk}>
                <Input name="thaluk" placeholder="Enter taluk" value={formData.thaluk} onChange={handleChange} error={errors.thaluk} />
              </Field>
              <Field label={<>Pin Code <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.pinCode}>
                <Input name="pinCode" placeholder="Enter pin code" value={formData.pinCode} onChange={handleChange} error={errors.pinCode} />
              </Field>
              <Field label={<>District <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.district}>
                <Input name="district" placeholder="Enter district" value={formData.district} onChange={handleChange} error={errors.district} />
              </Field>
            </Section>

            {/* ── Section: Contact ── */}
            <Section icon="mdi-phone-outline" title="Contact Information">
              <Field label="Phone" error={errors.phone}>
                <Input name="phone" placeholder="Enter phone number" value={formData.phone} onChange={handleChange} />
              </Field>
              <Field label="WhatsApp No" error={errors.whatsAppNo}>
                <Input name="whatsAppNo" placeholder="Enter WhatsApp number" value={formData.whatsAppNo} onChange={handleChange} />
              </Field>
              <Field label="Email" error={errors.eMail}>
                <Input name="eMail" type="email" placeholder="Enter email address" value={formData.eMail} onChange={handleChange} />
              </Field>
            </Section>

            {/* ── Section: Financial / Bank ── */}
            <Section icon="mdi-bank-outline" title="Bank Details">
              <Field label="Bank Name" error={errors.bank}>
                <Input name="bank" placeholder="Enter bank name" value={formData.bank} onChange={handleChange} />
              </Field>
              <Field label="Branch Name" error={errors.branch}>
                <Input name="branch" placeholder="Enter branch name" value={formData.branch} onChange={handleChange} />
              </Field>
              <Field label="Account Number" error={errors.accountNumber}>
                <Input name="accountNumber" placeholder="Enter account number" value={formData.accountNumber} onChange={handleChange} />
              </Field>
              <Field label="IFSC Code" error={errors.ifscCode}>
                <Input name="ifscCode" placeholder="Enter IFSC code" value={formData.ifscCode} onChange={handleChange} />
              </Field>
            </Section>

            {/* ── Section: ID Documents ── */}
            <Section icon="mdi-card-account-details-outline" title="Identity Documents">
              <Field label="Aadhar Number" error={errors.aadhar}>
                <Input name="aadhar" placeholder="Enter Aadhar number" value={formData.aadhar} onChange={handleChange} />
              </Field>
              <Field label="Ration Card Number" error={errors.rationCard}>
                <Input name="rationCard" placeholder="Enter ration card number" value={formData.rationCard} onChange={handleChange} />
              </Field>
            </Section>

            {/* ── Section: Other ── */}
            <Section icon="mdi-briefcase-outline" title="Other Information">
              <Field label="Occupation" error={errors.work}>
                <Input name="work" placeholder="Enter occupation" value={formData.work} onChange={handleChange} />
              </Field>
              <Field label="Educational Qualification" error={errors.educationalQualification}>
                <Input name="educationalQualification" placeholder="Enter qualification" value={formData.educationalQualification} onChange={handleChange} />
              </Field>
              <Field label="Income" error={errors.income}>
                <Input name="income" type="number" placeholder="Enter annual income" value={formData.income} onChange={handleChange} />
              </Field>
            </Section>

            {/* ── Section: Uploads ── */}
            <Section icon="mdi-image-outline" title="Photo & Signature">
              <Field label="Profile Picture">
                <input
                  type="file" name="profilePicture" accept="image/*"
                  onChange={handleChange}
                  style={{ ...inputStyle, padding: "7px 12px", cursor: "pointer" }}
                />
              </Field>
              <Field label="Signature">
                <input
                  type="file" name="signature" accept="image/*"
                  onChange={handleChange}
                  style={{ ...inputStyle, padding: "7px 12px", cursor: "pointer" }}
                />
              </Field>
            </Section>

            {/* ── Actions ── */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, paddingTop: 8, borderTop: "1px solid #EDF2F7", marginTop: 8 }}>
              <button
                type="submit"
                disabled={submitting}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "10px 28px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)",
                  color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700,
                  cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.7 : 1,
                  boxShadow: "0 3px 10px rgba(46,125,50,0.3)", transition: "all 0.2s",
                }}
              >
                <i className={`mdi ${submitting ? "mdi-loading mdi-spin" : "mdi-check-circle-outline"}`} />
                {submitting ? "Updating…" : "Update Member"}
              </button>
              <button
                type="button"
                onClick={() => navigate("/member/manage")}
                style={{
                  padding: "10px 20px", background: "#fff", color: "#4A5568",
                  border: "1.5px solid #E2E8F0", borderRadius: 10, fontSize: 13,
                  fontWeight: 600, cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default EditMember;
